// Rescheduling a job is "create the new one, cancel the old one": the reschedule endpoint can't take a
// time range or a pro request. The old job is cancelled only after the new one exists, so a failed
// create leaves the customer's job in place (ticket 19443: customers lost jobs to a 500 after the cancel).
//
// This order is only safe for a single job. gotidy refuses a new recurring plan while the address still
// has a plan with future jobs for that service (409), and a new one-time job joins the old plan and would
// be cancelled with it, so plan reschedules and job -> recurring plan reschedules keep cancelling first.

export const RESCHEDULE_CREATE_FAILED_MESSAGE =
  'We could not reschedule your job. Your original job is still scheduled. Please try again.';
export const RESCHEDULE_CANCEL_FAILED_MESSAGE =
  'We could not cancel your original job, so it was not rescheduled. Your original job is still scheduled.';
export const RESCHEDULE_BOTH_KEPT_MESSAGE =
  'Your new job request was sent, but we could not cancel your original job. Please cancel it from your Schedule.';

export interface RescheduleSteps<T> {
  create: () => Promise<T>;
  cancelOriginal: () => Promise<unknown>;
  // Takes the new job back out when the original can't be cancelled. Leave it out when the create
  // response has nothing to undo (a pro request answers 201 with no body).
  undoCreate?: (created: T) => Promise<unknown>;
}

export async function createThenCancelOriginal<T>(steps: RescheduleSteps<T>): Promise<T> {
  let created: T;
  try {
    created = await steps.create();
  } catch (err) {
    throw readableCreateError(err);
  }
  try {
    await steps.cancelOriginal();
  } catch (cancelErr) {
    throw await undoAfterFailedCancel(steps, created, cancelErr);
  }
  return created;
}

export function createdBookingId(result: any): number | null {
  return result?.booking_id || result?.id || null;
}

// Keeps the status code, since callers branch on it (402 opens the declined-card form). A 4xx carries a
// message meant for the customer; a 5xx or a network failure carries an exception or nothing.
function readableCreateError(err: any): any {
  if (isCustomerFacingRefusal(err)) {
    return err;
  }
  return { code: err?.code ?? err?.status, message: RESCHEDULE_CREATE_FAILED_MESSAGE, err };
}

function isCustomerFacingRefusal(err: any): boolean {
  const code = Number(err?.code ?? err?.status);
  return code >= 400 && code < 500 && typeof err?.message === 'string' && err.message.trim() !== '';
}

async function undoAfterFailedCancel<T>(steps: RescheduleSteps<T>, created: T, cancelErr: any): Promise<any> {
  if (steps.undoCreate) {
    try {
      await steps.undoCreate(created);
      return { code: cancelErr?.code, message: RESCHEDULE_CANCEL_FAILED_MESSAGE, err: cancelErr };
    } catch {
      // Falls through: both jobs exist, and the customer has to know.
    }
  }
  return { code: cancelErr?.code, message: RESCHEDULE_BOTH_KEPT_MESSAGE, err: cancelErr };
}
